import { ApPayee } from '@/components/ApPayee';
import { KeywordCont } from '@/components/KeywordCont';
import { Amount } from '@/features/invoice_reports/components/Search/components/Amount';
import { InvoiceMFFileStatusesSelect } from '@/features/invoice_reports/components/Search/components/InvoiceMFFileStatusesSelect';
import { PaymentStatusSelect } from '@/features/invoice_reports/components/Search/components/PaymentStatusSelect';
import { useElementWidth } from '@/hooks/useElementWidth';
import { BaseOption } from '@/hooks/useTypeToOptions';
import { ButtonV2, Form, Label } from '@moneyforward/ap-frontend-components';
import classnames from 'classnames/bind';
import { FC, memo, ReactNode } from 'react';
import { UseFormReturn } from 'react-hook-form';
import { useConfigForm } from '../../hooks/useConfigForm';
import { InvoiceReportsSearchFormData } from '../../hooks/useSearchForm';
import styles from '../../Search.module.scss';
import { AgentApplicantTargetOfficeMember } from '../AgentApplicantTargetOfficeMember';
import { DateRangeSelect } from '../DateRangeSelect';
import { FormContainer } from '../FormContainer';
import { FromInputSearch } from '../FromInputSearch';
import { InvoiceStatusesSelect } from '../InvoiceStatusesSelect';

const cx = classnames.bind(styles);

interface SearchFormProps
  extends Pick<
    ReturnType<typeof useConfigForm>,
    'customProps' | 'checkLengthValidationRules'
  > {
  form: UseFormReturn<InvoiceReportsSearchFormData>;
  onSubmit: (data: InvoiceReportsSearchFormData) => void;
  onReset: () => void;
  agentApplicantDefaultOptions?: BaseOption[];
  appPayeeDefaultOptions: BaseOption[];
  canAgentEntry: boolean;
  isLoading?: boolean;
  onActionButtonsWidthChange?: (width: number) => void;
}

const FormGroup: FC<{ children: ReactNode; className?: string }> = ({
  children,
  className,
}) => (
  <div className={cx('flex-1', 'search-row-group', className)}>{children}</div>
);

export const SearchForm = memo(
  ({
    form,
    onSubmit,
    onReset,
    agentApplicantDefaultOptions,
    customProps,
    checkLengthValidationRules,
    appPayeeDefaultOptions,
    canAgentEntry,
    isLoading = false,
    onActionButtonsWidthChange,
  }: SearchFormProps) => {
    const { control } = form;
    const { labels } = customProps;
    const actionButtonsRef = useElementWidth(onActionButtonsWidthChange);

    return (
      <Form<InvoiceReportsSearchFormData> formMethod={form} onSubmit={onSubmit}>
        {/* Flex-based layout matching Figma 24-column proportions */}
        <div className={cx('search-rows')}>
          {/* Row 1:
            - canAgentEntry: キーワード, 申請番号, 申請ステータス, 申請者, 支払状況, 添付請求書
            - !canAgentEntry: キーワード, 申請番号, 申請ステータス, 支払状況, 添付請求書
          */}
          <div className={cx('search-row')}>
            <FormGroup>
              <FormContainer className={cx('flex-6')}>
                <div>
                  <Label
                    text={labels.query.text}
                    tooltip={labels.query.tooltip}
                  />
                  <FromInputSearch
                    control={control}
                    inputProps={customProps?.inputProps?.query}
                    name='query'
                    inputId='query'
                    rules={checkLengthValidationRules}
                    testId='query-input'
                  />
                </div>
              </FormContainer>
              <FormGroup>
                <FormContainer className={cx('flex-2')}>
                  <KeywordCont
                    control={control}
                    name='number'
                    labelProps={labels.number}
                    inputProps={customProps?.inputProps?.number}
                    rules={checkLengthValidationRules}
                  />
                </FormContainer>
                <FormContainer className={cx('flex-4')}>
                  <div className={cx('select-wrapper')}>
                    <label className={cx('label-text')}>
                      {labels.status.text}
                    </label>
                    <InvoiceStatusesSelect control={control} name='status' />
                  </div>
                </FormContainer>
              </FormGroup>
            </FormGroup>

            <FormGroup>
              {canAgentEntry && (
                <FormContainer className={cx('flex-4')}>
                  <div className={cx('select-wrapper')}>
                    <label className={cx('label-text')}>
                      {labels.agent_applicant.text}
                    </label>
                    <AgentApplicantTargetOfficeMember
                      control={control}
                      name='agent_applicant_office_member_ids'
                      inputId='agent_applicant_office_member_ids'
                      defaultOptions={agentApplicantDefaultOptions}
                      selectProps={
                        customProps?.selectProps?.configAgentApplicant
                      }
                      per={200}
                      display_all_roles
                      include_agent_applicant
                    />
                  </div>
                </FormContainer>
              )}
              <FormContainer className={cx('flex-4')}>
                <div className={cx('select-wrapper')}>
                  <label className={cx('label-text')}>
                    {labels.payment_status.text}
                  </label>
                  <PaymentStatusSelect
                    control={control}
                    name='payment_status'
                  />
                </div>
              </FormContainer>
              <FormContainer className={cx('flex-4')}>
                <div className={cx('select-wrapper')}>
                  <label className={cx('label-text')}>
                    {labels.mf_file.text}
                  </label>
                  <InvoiceMFFileStatusesSelect
                    control={control}
                    name='mf_file'
                    formContext={form}
                  />
                </div>
              </FormContainer>
            </FormGroup>
          </div>

          {/* Row 2: 支払先, 合計金額, 作成日, 申請日 (all equal) */}
          <div className={cx('search-row')}>
            <FormContainer className={cx('flex-1')}>
              <div className={cx('select-wrapper-app-payee')}>
                <label className={cx('label-text')}>
                  {labels.ap_payee.text}
                </label>
                <ApPayee
                  control={control}
                  name='ap_payee_id'
                  inputId='payee'
                  formContext={undefined}
                  defaultOptions={appPayeeDefaultOptions}
                  selectProps={customProps?.selectProps?.configApPayee}
                />
              </div>
            </FormContainer>

            <FormContainer className={cx('flex-1')}>
              <div className={cx('total-amount-wrapper')}>
                <label className={cx('label-text')}>
                  {labels.total_amount.text}
                </label>
                <div className={cx('input-wrapper-number')}>
                  <Amount control={control} name='total_amount_from' />
                  <span className={cx('amount-range-separator')}>~</span>
                  <Amount control={control} name='total_amount_to' />
                </div>
              </div>
            </FormContainer>

            <FormContainer className={cx('flex-1')}>
              <DateRangeSelect
                control={control}
                name='created_date'
                labelProps={labels.created_date}
              />
            </FormContainer>

            <FormContainer className={cx('flex-1')}>
              <DateRangeSelect
                control={control}
                name='submitted_date'
                labelProps={labels.submitted_date}
              />
            </FormContainer>
          </div>

          {/* Row 3: 承認日, 費用計上日, 支払期日, 支払日 (all equal) */}
          <div className={cx('search-row')}>
            <FormContainer className={cx('flex-1')}>
              <DateRangeSelect
                control={control}
                name='approved_date'
                labelProps={labels.approved_date}
              />
            </FormContainer>

            <FormContainer className={cx('flex-1')}>
              <DateRangeSelect
                control={control}
                name='book_date'
                labelProps={labels.book_date}
              />
            </FormContainer>

            <FormContainer className={cx('flex-1')}>
              <DateRangeSelect
                control={control}
                name='due_date'
                labelProps={labels.due_date}
              />
            </FormContainer>

            <FormContainer className={cx('flex-1')}>
              <DateRangeSelect
                control={control}
                name='payment_date'
                labelProps={labels.payment_date}
              />
            </FormContainer>
          </div>
        </div>

        {/* Action Buttons */}
        <div ref={actionButtonsRef} className={cx('action-buttons')}>
          <ButtonV2
            size='sm'
            isTertiary
            onClick={onReset}
            className={cx('reset-button')}
          >
            {labels.reset_button}
          </ButtonV2>
          <ButtonV2
            size='sm'
            isSecondary
            type='submit'
            buttonContainerClassName={cx('search-button-container')}
            isLoading={isLoading}
            isDisabled={isLoading}
          >
            {labels.search_button}
          </ButtonV2>
        </div>
      </Form>
    );
  }
);

SearchForm.displayName = 'SearchForm';
